import { parseTaskDate } from '@modules/tarefas/utils/isOverdue'

/**
 * Date-boundary helpers for the `date_new_ux` <InputByType> inside the Tarefas
 * module.
 *
 * The backend serializes task dates as ISO `YYYY-MM-DD` (see
 * `_overview_helpers.format_due_date` / `JSONEncoderExtendedDataFrames`), but
 * `date_new_ux` operates entirely in `DD/MM/YYYY`: its inner `v-text-field` is
 * masked `DD/MM/AAAA`, so feeding it a raw ISO string (`2026-05-20`) gets
 * mangled by the mask into garbage (`20/26/0520`). Conversely, the component
 * emits `DD/MM/YYYY`, which `formatDate()` (ISO-only) and the overdue
 * comparison cannot read, and which Postgres' default datestyle rejects for
 * days > 12.
 *
 * These helpers normalize at the boundary: `toInputDate` going INTO the
 * editor, `toIsoDate` coming OUT. Both reuse the defensive multi-format
 * `parseTaskDate` so ISO and `DD/MM/YYYY` (and timed variants) all round-trip.
 */

/**
 * Converts a stored/backend date value to the `DD/MM/YYYY` string expected by
 * `date_new_ux`. Returns '' for empty/unparseable input.
 *
 * @param {string|Date|number} value
 * @returns {string}
 */
export function toInputDate(value) {
  if (!value) return ''
  const parsed = parseTaskDate(value)
  return parsed ? parsed.format('DD/MM/YYYY') : ''
}

/**
 * Converts a `date_new_ux` value (`DD/MM/YYYY`, possibly partial while the user
 * is still typing) to the ISO `YYYY-MM-DD` the backend stores and the display
 * utils expect. Returns `null` when the value is empty OR not yet a
 * complete/valid date — so partial keystrokes (e.g. "01/05/2") are never
 * persisted.
 *
 * @param {string|Date|number} value
 * @returns {string|null}
 */
export function toIsoDate(value) {
  if (!value) return null
  const str = String(value)
  // Only convert COMPLETE dates — partial keystrokes ("01/05/2", "20/05")
  // must yield null so they are never persisted. parseTaskDate alone is too
  // lenient here (it would coerce "01/05/2" into a valid date), so we gate on
  // the shape first: DD/MM/YYYY (date_new_ux output) or ISO YYYY-MM-DD[...]
  // (an already-normalized value). Mirrors `_isCompleteDate` in apiTarefas.js.
  const isDayFirst = /^\d{2}\/\d{2}\/\d{4}$/.test(str)
  const isIso = /^\d{4}-\d{2}-\d{2}/.test(str)
  if (!isDayFirst && !isIso) return null
  const parsed = parseTaskDate(str)
  return parsed ? parsed.format('YYYY-MM-DD') : null
}
